/**
 * Phase 22 Plan 22-03 — ClientCreatePage
 *
 * Route: /clients/new
 * Permission gate: clients.create
 *
 * zod-validated form → POST /api/clients → redirect to /clients/:id.
 */

import { ArrowLeftIcon } from '@phosphor-icons/react';
import { type JSX, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { ClientFormFields } from '@/components/clients/ClientFormFields';
import { clientFormSchema, type ClientFormValues } from '@/components/clients/clientFormSchema';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateClientMutation } from '@/hooks/useClientQueries';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';

export function ClientCreatePage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createMutation = useCreateClientMutation();

  const form = useZodForm({
    schema: clientFormSchema,
    defaultValues: {
      name: '',
      code: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      gstNumber: '',
      panNumber: '',
      notes: '',
    },
  });

  if (!permissions.includes('clients.create')) {
    return <Navigate to="/clients" replace />;
  }

  const onSubmit = form.handleSubmit((values: ClientFormValues) => {
    setErrorMessage(null);
    createMutation.mutate(
      {
        name: values.name,
        code: values.code,
        ...(values.contactName && values.contactName !== '' ? { contactName: values.contactName } : {}),
        ...(values.contactEmail && values.contactEmail !== '' ? { contactEmail: values.contactEmail } : {}),
        ...(values.contactPhone && values.contactPhone !== '' ? { contactPhone: values.contactPhone } : {}),
        ...(values.address && values.address !== '' ? { address: values.address } : {}),
        ...(values.gstNumber && values.gstNumber !== '' ? { gstNumber: values.gstNumber } : {}),
        ...(values.panNumber && values.panNumber !== '' ? { panNumber: values.panNumber } : {}),
        ...(values.notes && values.notes !== '' ? { notes: values.notes } : {}),
      },
      {
        onSuccess: (client) => {
          toast.success(`Client "${client.name}" created`);
          void navigate(`/clients/${String(client.id)}`);
        },
        onError: (err) => {
          const msg = err instanceof ApiError ? (err.detail ?? err.message) : 'Unexpected error';
          setErrorMessage(msg);
          toast.error('Could not create client', { description: msg });
        },
      },
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2">
          <Link to="/clients">
            <ArrowLeftIcon size={16} /> Clients
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">New Client</h1>
        <p className="text-muted-foreground">Create a new client record.</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Client Details</CardTitle>
          <CardDescription>Name and Code are required.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="client-create-form">
            <ClientFormFields form={form} />

            {errorMessage !== null && (
              <div
                className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="client-create-error"
              >
                {errorMessage}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                asChild
                disabled={createMutation.isPending}
              >
                <Link to="/clients">Cancel</Link>
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                data-testid="client-create-submit"
              >
                {createMutation.isPending ? 'Creating…' : 'Create Client'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
